import React from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";

import MarketingHome from "@/pages/marketing/Home";
import TestBooking from "@/pages/marketing/TestBooking";
import LabSignup from "@/pages/marketing/LabSignup";
import Login from "@/pages/auth/Login";
import Register from "@/pages/auth/Register";
import ForgotPassword from "@/pages/auth/ForgotPassword";
import ResetPassword from "@/pages/auth/ResetPassword";

import PatientDashboard from "@/pages/patient/Dashboard";
import PatientUpload from "@/pages/patient/Upload";
import PatientReports from "@/pages/patient/Reports";
import PatientBookings from "@/pages/patient/Bookings";
import PatientFamily from "@/pages/patient/Family";
import PatientPrescriptions from "@/pages/patient/Prescriptions";
import PatientTrends from "@/pages/patient/Trends";
import PatientReferrals from "@/pages/patient/Referrals";
import PatientWallet from "@/pages/patient/Wallet";
import SmartReport from "@/pages/report/SmartReport";
import PublicSmartReport from "@/pages/report/PublicSmartReport";
import { PrintOnlyReport, PrintOnlyPublic } from "@/pages/report/PrintOnlyView";
import PublicBooking from "@/pages/public/PublicBooking";

import LabDashboard from "@/pages/lab/Dashboard";
import LabReports from "@/pages/lab/Reports";
import LabReportReview from "@/pages/lab/ReportReview";
import LabBranding from "@/pages/lab/Branding";
import LabPatients from "@/pages/lab/Patients";
import LabBookings from "@/pages/lab/Bookings";
import LabBookingRequests from "@/pages/lab/BookingRequests";
import LabCatalogue from "@/pages/lab/Catalogue";
import LabPackages from "@/pages/lab/Packages";
import LabRules from "@/pages/lab/Rules";
import LabLIS from "@/pages/lab/LIS";
import LabEmailIntake from "@/pages/lab/EmailIntake";
import LabAnalytics from "@/pages/lab/Analytics";
import LabReferrals from "@/pages/lab/Referrals";
import LabReviews from "@/pages/lab/Reviews";
import { LabTeam, LabBilling, LabSettings } from "@/pages/lab/Account";

import { AdminDashboard, AdminLabs, AdminBookings, AdminReports, AdminBilling, AdminMarketing } from "@/pages/admin/Admin";
import AdminPlatformSettings from "@/pages/admin/PlatformSettings";
import AdminLetterheadTemplates from "@/pages/admin/LetterheadTemplates";
import AdminKnowledgeBase from "@/pages/admin/KnowledgeBase";
import PricingManagement from "@/pages/admin/PricingManagement";
import AdminMarketplaceControl from "@/pages/admin/MarketplaceControl";
import { AdminUsers, AdminUserProfile } from "@/pages/admin/Users";
import AdminLabProfile from "@/pages/admin/LabProfile";

function ProtectedRoute({ children, roles }) {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/" replace />;
  return children;
}

/**
 * Every authenticated user always lands on their role-specific dashboard when
 * they hit the root URL. Unauthenticated visitors continue to see the marketing
 * home. Works consistently on desktop and mobile since it runs client-side on
 * every render of "/".
 */
function RoleHome() {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <MarketingHome />;
  if (user.role === "patient") return <Navigate to="/patient" replace />;
  if (user.role === "lab_admin") return <Navigate to="/lab" replace />;
  if (user.role === "super_admin") return <Navigate to="/admin" replace />;
  return <MarketingHome />;
}

export default function App() {
  return (
    <Routes>
      {/* Public */}
      <Route path="/" element={<RoleHome />} />
      <Route path="/book-tests" element={<TestBooking />} />
      <Route path="/for-labs" element={<LabSignup />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />

      {/* Smart report is accessible when logged in */}
      <Route path="/report/:id" element={<ProtectedRoute><SmartReport /></ProtectedRoute>} />
      <Route path="/l/:slug/r/:token" element={<PublicSmartReport />} />
      {/* Server-side PDF rendering surfaces (used by Playwright) — no auth guard because
         auth is passed via ?auth=<jwt> query param, or the public share token in the URL. */}
      <Route path="/pdf-render/:reportId" element={<PrintOnlyReport />} />
      <Route path="/pdf-render-public/:slug/:token" element={<PrintOnlyPublic />} />
      <Route path="/l/:slug/book" element={<PublicBooking />} />

      {/* Patient */}
      <Route path="/patient" element={<ProtectedRoute roles={["patient"]}><PatientDashboard /></ProtectedRoute>} />
      <Route path="/patient/upload" element={<ProtectedRoute roles={["patient"]}><PatientUpload /></ProtectedRoute>} />
      <Route path="/patient/reports" element={<ProtectedRoute roles={["patient"]}><PatientReports /></ProtectedRoute>} />
      <Route path="/patient/bookings" element={<ProtectedRoute roles={["patient"]}><PatientBookings /></ProtectedRoute>} />
      <Route path="/patient/family" element={<ProtectedRoute roles={["patient"]}><PatientFamily /></ProtectedRoute>} />
      <Route path="/patient/prescriptions" element={<ProtectedRoute roles={["patient"]}><PatientPrescriptions /></ProtectedRoute>} />
      <Route path="/patient/trends" element={<ProtectedRoute roles={["patient"]}><PatientTrends /></ProtectedRoute>} />
      <Route path="/patient/referrals" element={<ProtectedRoute roles={["patient"]}><PatientReferrals /></ProtectedRoute>} />
      <Route path="/patient/wallet" element={<ProtectedRoute roles={["patient"]}><PatientWallet /></ProtectedRoute>} />

      {/* Lab */}
      <Route path="/lab" element={<ProtectedRoute roles={["lab_admin"]}><LabDashboard /></ProtectedRoute>} />
      <Route path="/lab/reports" element={<ProtectedRoute roles={["lab_admin"]}><LabReports /></ProtectedRoute>} />
      <Route path="/lab/reports/:id/review" element={<ProtectedRoute roles={["lab_admin", "super_admin"]}><LabReportReview /></ProtectedRoute>} />
      <Route path="/lab/patients" element={<ProtectedRoute roles={["lab_admin"]}><LabPatients /></ProtectedRoute>} />
      <Route path="/lab/bookings" element={<ProtectedRoute roles={["lab_admin"]}><LabBookings /></ProtectedRoute>} />
      <Route path="/lab/booking-requests" element={<ProtectedRoute roles={["lab_admin"]}><LabBookingRequests /></ProtectedRoute>} />
      <Route path="/lab/branding" element={<ProtectedRoute roles={["lab_admin"]}><LabBranding /></ProtectedRoute>} />
      <Route path="/lab/catalogue" element={<ProtectedRoute roles={["lab_admin"]}><LabCatalogue /></ProtectedRoute>} />
      <Route path="/lab/packages" element={<ProtectedRoute roles={["lab_admin"]}><LabPackages /></ProtectedRoute>} />
      <Route path="/lab/rules" element={<ProtectedRoute roles={["lab_admin"]}><LabRules /></ProtectedRoute>} />
      <Route path="/lab/lis" element={<ProtectedRoute roles={["lab_admin"]}><LabLIS /></ProtectedRoute>} />
      <Route path="/lab/email-intake" element={<ProtectedRoute roles={["lab_admin"]}><LabEmailIntake /></ProtectedRoute>} />
      <Route path="/lab/analytics" element={<ProtectedRoute roles={["lab_admin"]}><LabAnalytics /></ProtectedRoute>} />
      <Route path="/lab/reviews" element={<ProtectedRoute roles={["lab_admin"]}><LabReviews /></ProtectedRoute>} />
      <Route path="/lab/referrals" element={<ProtectedRoute roles={["lab_admin"]}><LabReferrals /></ProtectedRoute>} />
      <Route path="/lab/team" element={<ProtectedRoute roles={["lab_admin"]}><LabTeam /></ProtectedRoute>} />
      <Route path="/lab/billing" element={<ProtectedRoute roles={["lab_admin"]}><LabBilling /></ProtectedRoute>} />
      <Route path="/lab/settings" element={<ProtectedRoute roles={["lab_admin"]}><LabSettings /></ProtectedRoute>} />

      {/* Super admin */}
      <Route path="/admin" element={<ProtectedRoute roles={["super_admin"]}><AdminDashboard /></ProtectedRoute>} />
      <Route path="/admin/labs" element={<ProtectedRoute roles={["super_admin"]}><AdminLabs /></ProtectedRoute>} />
      <Route path="/admin/bookings" element={<ProtectedRoute roles={["super_admin"]}><AdminBookings /></ProtectedRoute>} />
      <Route path="/admin/reports" element={<ProtectedRoute roles={["super_admin"]}><AdminReports /></ProtectedRoute>} />
      <Route path="/admin/billing" element={<ProtectedRoute roles={["super_admin"]}><AdminBilling /></ProtectedRoute>} />
      <Route path="/admin/marketing" element={<ProtectedRoute roles={["super_admin"]}><AdminMarketing /></ProtectedRoute>} />
      <Route path="/admin/platform-settings" element={<ProtectedRoute roles={["super_admin"]}><AdminPlatformSettings /></ProtectedRoute>} />
      <Route path="/admin/letterhead-templates" element={<ProtectedRoute roles={["super_admin"]}><AdminLetterheadTemplates /></ProtectedRoute>} />
      <Route path="/admin/knowledge-base" element={<ProtectedRoute roles={["super_admin"]}><AdminKnowledgeBase /></ProtectedRoute>} />
      <Route path="/admin/pricing" element={<ProtectedRoute roles={["super_admin"]}><PricingManagement /></ProtectedRoute>} />
      <Route path="/admin/marketplace-control" element={<ProtectedRoute roles={["super_admin"]}><AdminMarketplaceControl /></ProtectedRoute>} />
      <Route path="/admin/users" element={<ProtectedRoute roles={["super_admin"]}><AdminUsers /></ProtectedRoute>} />
      <Route path="/admin/users/:id" element={<ProtectedRoute roles={["super_admin"]}><AdminUserProfile /></ProtectedRoute>} />
      <Route path="/admin/labs/:id" element={<ProtectedRoute roles={["super_admin"]}><AdminLabProfile /></ProtectedRoute>} />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
